/**
 * "Divide into sections" APPLY — pure doc logic over BoardDoc (no React, no
 * DOM). The caller wraps this in `session.batch` so the whole application is
 * ONE transaction = one sync unit = one undo step (same contract as
 * arrange.ts's applyArrange).
 *
 * What it writes (F-139 — ONE labeled column, not two):
 *  - the "Mood" column: a NEW one directly to the right of the Original
 *    Script column (index 1 — the F-28 clamp keeps the source leftmost either
 *    way), or the sheet's EXISTING one when the script is being divided again
 *    (2026-09-11: re-dividing rewrites that column in place rather than
 *    minting a second "Mood" beside it). Every analyzed row gets, in that one
 *    cell, TWO things: its MOOD as the text — from the analysis's mood spans,
 *    so the value changes on the exact row the mood changed — and its TOPIC
 *    as the cell's fill color, the same color wherever that topic appears. A
 *    topic split around a joke therefore reads as one color with the mood
 *    text changing inside it, which is the whole point: same scene before
 *    and after, the mood marked to the sentence. On a reused column, rows the
 *    analysis did not cover (empty script rows, rows whose text was deleted
 *    since the last division) are CLEARED, so a stale label never lingers to
 *    open a music run of its own.
 *    (Before F-139 there was a "Section" column carrying the topic's TITLE;
 *    the title now lives only in the preview — the color is the label.)
 *  - the "BGM" column (F-126), one music slot per mood run: the existing one
 *    when the sheet has it (re-analyzing must never orphan the stems already
 *    chosen), else a new EMPTY one right after Mood — always, unless a caller
 *    opts out; a divided script has its slots by definition.
 *  - on a board divided BEFORE F-139, the old "Section" column is REMOVED.
 *    `bgmColumns` keys the music runs off "Section" whenever one exists (so
 *    those boards' section slots keep working untouched), which means a Mood
 *    column added beside it would never get slots of its own — the BGM
 *    column has to follow the moods the user just asked for. The topic that
 *    column named is now the Mood column's color.
 *  - stems the new runs leave STRANDED — a stem on a row that is no longer
 *    the first row of its run, because the boundaries moved — are carried up
 *    to that run's first row, appended after whatever it already holds.
 *    Coverage looks only at a run's top cell and the slot chip only draws
 *    there, so a stem left below would be music nobody can see or count.
 *    Nothing here ever drops one.
 *
 * Nothing outside those columns is touched — the script is read, never
 * written — so there is no C6 stale-text guard to run here: a source cell
 * edited while the preview sat open still belongs to the topic the user just
 * looked at. The one live check is C4: a row DELETED meanwhile is skipped and
 * counted, never resurrected.
 */
import {
  BGM_COLUMN_NAME,
  bgmStemsOf,
  boardBgmCoverage,
  findColumnByName,
  MOOD_COLUMN_NAME,
  SECTION_COLUMN_NAME,
} from "@kola/shared";
import type { BoardDoc } from "@kola/shared";

import { BGM_COLUMN_WIDTH } from "../bgm/ensure-column.js";
import type { MoodPreview, SectionPreview } from "./store.js";

// Re-exported for the existing call sites; the names themselves live in
// @kola/shared, because the SERVER has to find these columns too (the stems
// tag is derived from the live doc).
export { BGM_COLUMN_NAME, MOOD_COLUMN_NAME, SECTION_COLUMN_NAME };

const MOOD_COLUMN_WIDTH = 160;

export interface ApplySectionsOptions {
  /**
   * The mood spans the Mood column's TEXT is written from — one mood per
   * ROW, changing on the exact row the analysis marked. A row no span covers
   * (an analysis from a server built before F-139, which knows one mood per
   * topic) falls back to its topic's mood.
   */
  moods?: readonly MoodPreview[] | undefined;
  /**
   * F-126: mint the empty "BGM" column — one music-stem slot per mood run, on
   * the run's TOP row. Default TRUE (2026-09-03: it was a checkbox; a divided
   * script always has its slots now, and a board divided before the slots
   * existed grows the column on its own — features/bgm/ensure-column).
   * Skipped when the column already exists (re-analyzing a script must not
   * orphan the stems already chosen).
   */
  withBgm?: boolean;
  origin?: unknown;
}

export interface ApplySectionsResult {
  /** Rows that received a mood cell (text and/or color). */
  labeled: number;
  /** Analyzed rows deleted while the preview sat open (C4) — skipped. */
  skipped: number;
  moodColId: string;
  /** True when the sheet already had a Mood column and it was rewritten in place. */
  moodColReused: boolean;
  /** True when a pre-F-139 "Section" column was removed so the runs follow Mood. */
  sectionColRemoved: boolean;
  /** Stems carried up to their run's first row because the run boundaries moved. */
  movedStems: number;
  /** The BGM column — minted now, or the existing one this apply reused. */
  bgmColId?: string;
}

export function applySections(
  board: BoardDoc,
  sheetId: string,
  sections: readonly SectionPreview[],
  mintColId: () => string,
  options: ApplySectionsOptions = {},
): ApplySectionsResult {
  const { origin } = options;
  const withBgm = options.withBgm ?? true;
  const moodOfRow = new Map<string, string>();
  for (const span of options.moods ?? []) {
    for (const rowId of span.rowIds) moodOfRow.set(rowId, span.mood);
  }
  return board.transact(() => {
    const present = new Set(board.sortedRowIds(sheetId));
    // Located BEFORE anything is added, so a re-analysis finds the real
    // existing columns rather than ones this call is about to mint.
    const existingMoodColId = findColumnByName(board, sheetId, MOOD_COLUMN_NAME);
    const existingSectionColId = findColumnByName(board, sheetId, SECTION_COLUMN_NAME);
    const existingBgmColId = findColumnByName(board, sheetId, BGM_COLUMN_NAME);

    let moodColId = existingMoodColId;
    if (moodColId === undefined) {
      moodColId = mintColId();
      board.addColumn(sheetId, moodColId, {
        index: 1,
        name: MOOD_COLUMN_NAME,
        width: MOOD_COLUMN_WIDTH,
        origin,
      });
    }

    // A board divided before F-139: while a "Section" column exists the music
    // runs are ITS runs (`bgmColumns`), and the moods just written would get
    // no slots. The topic it named is the Mood column's color from here on.
    const sectionColRemoved =
      existingSectionColId !== undefined && board.deleteColumn(sheetId, existingSectionColId, origin);

    // F-126: the stem slots. Left EMPTY when minted — an AI never picks the
    // music; it only says what the mood is, and the sound designer answers.
    let bgmColId: string | undefined = existingBgmColId;
    if (withBgm && bgmColId === undefined) {
      bgmColId = mintColId();
      board.addColumn(sheetId, bgmColId, {
        index: board.sortedColIds(sheetId).indexOf(moodColId) + 1,
        name: BGM_COLUMN_NAME,
        width: BGM_COLUMN_WIDTH,
        origin,
      });
    }

    const result: ApplySectionsResult = {
      labeled: 0,
      skipped: 0,
      moodColId,
      moodColReused: existingMoodColId !== undefined,
      sectionColRemoved,
      movedStems: 0,
    };
    if (bgmColId !== undefined) result.bgmColId = bgmColId;

    const written = new Set<string>();
    for (const section of sections) {
      for (const rowId of section.rowIds) {
        if (!present.has(rowId)) {
          result.skipped++;
          continue;
        }
        const mood = moodOfRow.get(rowId) ?? section.mood;
        if (mood !== "") {
          board.setCellValueAsHuman(sheetId, rowId, moodColId, mood, origin);
        } else if (board.getCellText(sheetId, rowId, moodColId) !== "") {
          // A blank mood on a reused column must not keep last time's word.
          board.setCellValueAsHuman(sheetId, rowId, moodColId, null, origin);
        }
        board.setCellBg(sheetId, rowId, moodColId, section.color, origin);
        written.add(rowId);
        result.labeled++;
      }
    }

    if (existingMoodColId !== undefined) {
      // Dividing again: a row the new analysis did not cover loses the old
      // division's label and color, else it would open a run of its own.
      for (const rowId of present) {
        if (written.has(rowId)) continue;
        if (board.getCellText(sheetId, rowId, moodColId) !== "") {
          board.setCellValueAsHuman(sheetId, rowId, moodColId, null, origin);
        }
        if (board.getCellBg(sheetId, rowId, moodColId) !== undefined) {
          board.setCellBg(sheetId, rowId, moodColId, undefined, origin);
        }
      }
    }

    if (bgmColId !== undefined) {
      result.movedStems = moveStrandedStems(board, sheetId, bgmColId, origin);
    }
    return result;
  }, origin);
}

/**
 * Carry every stem that sits BELOW its run's first row up onto that row.
 *
 * The runs are read back from the doc AFTER the labels are written, through
 * the same `boardBgmCoverage` the slot layer and the server's stems tag use —
 * so "first row" here is exactly the cell the chip will draw on and the
 * count will look at. A stem on a row no run claims (an unlabeled row) is
 * left where it is: it belongs to no mood, and guessing one would be worse
 * than leaving the designer's placement alone.
 */
function moveStrandedStems(
  board: BoardDoc,
  sheetId: string,
  bgmColId: string,
  origin: unknown,
): number {
  let moved = 0;
  for (const run of boardBgmCoverage(board, sheetId).runs) {
    for (const rowId of run.rowIds) {
      if (rowId === run.topRowId) continue;
      const slot = board.getCellBgm(sheetId, rowId, bgmColId);
      if (slot === undefined) continue;
      for (const stem of bgmStemsOf(slot)) {
        board.addCellBgmStem(sheetId, run.topRowId, bgmColId, stem, origin);
        moved++;
      }
      board.setCellValueAsHuman(sheetId, rowId, bgmColId, null, origin);
    }
  }
  return moved;
}
